import React, { useState, useEffect } from 'react';
import {
  FiSearch,
  FiCalendar,
  FiUser,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiClock as FiClockIcon,
  FiRefreshCw,
  FiLoader,
  FiAlertCircle,
  FiCheck,
  FiEdit,
  FiTrash2,
  FiMessageSquare,
  FiUsers,
  FiBook
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../api';

const DemoRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    institutionType: '',
    fromDate: '',
    toDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    notes: '',
    assignedTo: '',
    scheduledDateTime: ''
  });
  const [counts, setCounts] = useState({
    pending: 0,
    reviewed: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0,
    total: 0
  });
  const [updating, setUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const statusLabels = {
    pending: 'Pending',
    reviewed: 'Reviewed',
    scheduled: 'Scheduled',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };

  const institutionTypes = {
    school: 'School (K-12)',
    pu_college: 'PU College',
    degree_college: 'Degree College',
    coaching_center: 'Coaching Center',
    university: 'University',
    others: 'Others'
  };

  const institutionTypeColors = {
    school: 'bg-blue-50 text-blue-700',
    pu_college: 'bg-purple-50 text-purple-700',
    degree_college: 'bg-indigo-50 text-indigo-700',
    coaching_center: 'bg-orange-50 text-orange-700',
    university: 'bg-emerald-50 text-emerald-700',
    others: 'bg-gray-50 text-gray-700'
  };

  useEffect(() => {
    fetchRequests();
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.institutionType) params.append('institutionType', filters.institutionType);
      if (searchTerm) params.append('search', searchTerm);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const response = await api.get(`/api/super-admin/demo-requests?${params.toString()}`);
      setRequests(response.data.data.requests);
      setPagination(response.data.data.pagination);
    } catch (err) {
      console.error('Error fetching demo requests:', err);
      setError('Failed to load demo requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const response = await api.get('/api/super-admin/demo-requests?limit=1');
      setCounts(response.data.data.counts);
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedRequest) return;
    
    setUpdating(true);
    try {
      const payload = {
        status: statusUpdate.status,
        notes: statusUpdate.notes || '',
        assignedTo: statusUpdate.assignedTo || ''
      };
      
      if (statusUpdate.status === 'scheduled' && statusUpdate.scheduledDateTime) {
        payload.scheduledDateTime = statusUpdate.scheduledDateTime;
      }

      await api.put(`/api/super-admin/demo-requests/${selectedRequest._id}/status`, payload);
      setShowStatusModal(false);
      setStatusUpdate({ status: '', notes: '', assignedTo: '', scheduledDateTime: '' });
      await fetchRequests();
      await fetchCounts();
      // Also refresh the detail view if open
      if (showDetailModal) {
        const response = await api.get(`/api/super-admin/demo-requests/${selectedRequest._id}`);
        setSelectedRequest(response.data.data);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    
    try {
      await api.delete(`/api/super-admin/demo-requests/${deleteTargetId}`);
      setShowDeleteConfirm(false);
      setDeleteTargetId(null);
      await fetchRequests();
      await fetchCounts();
      if (showDetailModal && selectedRequest?._id === deleteTargetId) {
        setShowDetailModal(false);
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error('Error deleting request:', err);
      alert('Failed to delete request. Please try again.');
    }
  };

  const openStatusModal = (request) => {
    setSelectedRequest(request);
    setStatusUpdate({
      status: request.status || 'pending',
      notes: request.notes || '',
      assignedTo: request.assignedTo || '',
      scheduledDateTime: request.scheduledDateTime || ''
    });
    setShowStatusModal(true);
  };

  const openDetailModal = async (request) => {
    try {
      const response = await api.get(`/api/super-admin/demo-requests/${request._id}`);
      setSelectedRequest(response.data.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching request details:', err);
      alert('Failed to fetch request details');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      reviewed: 'bg-blue-100 text-blue-800',
      scheduled: 'bg-green-100 text-green-800',
      completed: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.pending;
  };

  const renderStatusBadge = (status) => (
    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(status)}`}>
      {statusLabels[status] || status}
    </span>
  );

  const renderInstitutionType = (type, other) => {
    if (type === 'others' && other) return other;
    return institutionTypes[type] || type;
  };

  const StatCard = ({ label, value, color, icon: Icon }) => (
    <div className={`bg-white rounded-xl border border-gray-100 p-4 shadow-sm`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );

  const StatusUpdateModal = () => (
    <AnimatePresence>
      {showStatusModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowStatusModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FiEdit className="w-5 h-5 text-white" />
                <h2 className="text-lg font-semibold text-white">Update Status</h2>
              </div>
              <button
                onClick={() => setShowStatusModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white/80 hover:text-white"
              >
                <FiXCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="space-y-4">
                {/* Institution Info */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{selectedRequest?.institutionName}</p>
                  <p className="text-xs text-gray-500">Request #: {selectedRequest?._id?.slice(-6).toUpperCase()}</p>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Status *</label>
                  <select
                    value={statusUpdate.status}
                    onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Scheduled Date & Time - Show when status is scheduled */}
                {statusUpdate.status === 'scheduled' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Scheduled Date & Time</label>
                    <input
                      type="datetime-local"
                      value={statusUpdate.scheduledDateTime}
                      onChange={(e) => setStatusUpdate({ ...statusUpdate, scheduledDateTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}

                {/* Assigned To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Assigned To</label>
                  <input
                    type="text"
                    value={statusUpdate.assignedTo}
                    onChange={(e) => setStatusUpdate({ ...statusUpdate, assignedTo: e.target.value })}
                    placeholder="Enter person's name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                  <textarea
                    value={statusUpdate.notes}
                    onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e.target.value })}
                    rows={3}
                    placeholder="Add notes about this request..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={updating}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updating ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    Update Status
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const DeleteConfirmModal = () => (
    <AnimatePresence>
      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiTrash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Demo Request?</h3>
              <p className="text-gray-500 text-sm">
                This action cannot be undone. Are you sure you want to delete this demo request?
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const DetailModal = () => (
    <AnimatePresence>
      {showDetailModal && selectedRequest && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowDetailModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FiEye className="w-5 h-5 text-white" />
                <div>
                  <h2 className="text-lg font-semibold text-white">Demo Request Details</h2>
                  <p className="text-xs text-gray-400">#{selectedRequest._id.slice(-6).toUpperCase()}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/80 hover:text-white"
              >
                <FiXCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="space-y-6">
                {/* Status */}
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-500">Status:</span>
                  {renderStatusBadge(selectedRequest.status)}
                </div>

                {/* Institution Details */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FiBook className="w-4 h-4" /> Institution Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{selectedRequest.institutionName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="font-medium text-gray-900">
                        {renderInstitutionType(selectedRequest.institutionType, selectedRequest.otherInstitutionType)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{selectedRequest.institutionEmail}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{selectedRequest.institutionPhone}</p>
                    </div>
                  </div>
                </div>

                {/* Point of Contact */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FiUser className="w-4 h-4" /> Point of Contact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{selectedRequest.pocName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{selectedRequest.pocEmail}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{selectedRequest.pocPhone}</p>
                    </div>
                  </div>
                </div>

                {/* Demo Preferences */}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FiCalendar className="w-4 h-4" /> Demo Preferences
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">Preferred Date</p>
                      <p className="font-medium text-gray-900">{formatDate(selectedRequest.preferredDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Preferred Time</p>
                      <p className="font-medium text-gray-900">{selectedRequest.preferredTime}</p>
                    </div>
                    {selectedRequest.scheduledDateTime && (
                      <div>
                        <p className="text-xs text-gray-500">Scheduled Date & Time</p>
                        <p className="font-medium text-green-600">{formatDateTime(selectedRequest.scheduledDateTime)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Message & Notes */}
                {selectedRequest.message && (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <FiMessageSquare className="w-4 h-4" /> Message
                    </h3>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedRequest.message}</p>
                  </div>
                )}

                {selectedRequest.notes && (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <FiMessageSquare className="w-4 h-4" /> Notes
                    </h3>
                    <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">{selectedRequest.notes}</p>
                  </div>
                )}

                {/* Assigned To */}
                {selectedRequest.assignedTo && (
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <FiUser className="w-4 h-4" /> Assigned To
                    </h3>
                    <p className="font-medium text-gray-900">{selectedRequest.assignedTo}</p>
                  </div>
                )}

                {/* Timestamps */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-500">
                    <div>
                      <p>Created: {formatDateTime(selectedRequest.createdAt)}</p>
                    </div>
                    <div>
                      <p>Updated: {formatDateTime(selectedRequest.updatedAt)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-wrap justify-end gap-3">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedRequest(null);
                }}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  openStatusModal(selectedRequest);
                  setShowDetailModal(false);
                }}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <FiEdit className="w-4 h-4" />
                Update Status
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiUsers className="w-6 h-6 text-indigo-600" />
              Demo Requests
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage and track all demo requests</p>
          </div>
          <button
            onClick={() => { fetchRequests(); fetchCounts(); }}
            className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 shadow-sm"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <StatCard label="Total" value={counts.total || 0} color="bg-gray-600" icon={FiUsers} />
          <StatCard label="Pending" value={counts.pending || 0} color="bg-amber-600" icon={FiClockIcon} />
          <StatCard label="Reviewed" value={counts.reviewed || 0} color="bg-blue-600" icon={FiEye} />
          <StatCard label="Scheduled" value={counts.scheduled || 0} color="bg-green-600" icon={FiCalendar} />
          <StatCard label="Completed" value={counts.completed || 0} color="bg-purple-600" icon={FiCheckCircle} />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={filters.institutionType}
              onChange={(e) => setFilters({ ...filters, institutionType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            >
              <option value="">All Types</option>
              <option value="school">School (K-12)</option>
              <option value="pu_college">PU College</option>
              <option value="degree_college">Degree College</option>
              <option value="coaching_center">Coaching Center</option>
              <option value="university">University</option>
              <option value="others">Others</option>
            </select>

            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              placeholder="From Date"
            />

            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              placeholder="To Date"
            />
          </div>

          <div className="flex justify-end mt-3">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilters({ status: '', institutionType: '', fromDate: '', toDate: '' });
                setPagination({ ...pagination, page: 1 });
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-500">Loading demo requests...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
            <button
              onClick={fetchRequests}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Try Again
            </button>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <FiUsers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No demo requests found</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institution
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preferred Date
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 sm:px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{request.institutionName}</p>
                          <p className="text-xs text-gray-500">{request.institutionEmail}</p>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div>
                          <p className="text-sm text-gray-900">{request.pocName}</p>
                          <p className="text-xs text-gray-500">{request.pocPhone}</p>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${institutionTypeColors[request.institutionType] || 'bg-gray-100 text-gray-700'}`}>
                          {renderInstitutionType(request.institutionType, request.otherInstitutionType)}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="text-sm text-gray-900">{formatDate(request.preferredDate)}</div>
                        <div className="text-xs text-gray-500">{request.preferredTime}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        {renderStatusBadge(request.status)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openDetailModal(request)}
                            className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openStatusModal(request)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Update Status"
                          >
                            <FiEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setDeleteTargetId(request._id);
                              setShowDeleteConfirm(true);
                            }}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {(pagination.page - 1) * pagination.limit + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <DetailModal />
      <StatusUpdateModal />
      <DeleteConfirmModal />
    </div>
  );
};

export default DemoRequests;